import type { Column, ColumnDef } from '@tanstack/vue-table'
import { Button } from '~/components/ui/button'
import BarsArrowUp from '~/components/BarsArrowUp.vue'
import DataTableFilterableHeader from '~/components/ui/table/DataTableFilterableHeader.vue'
import { UserAccountRoles } from '~/constants/userRoles'

export type UserType = 'active' | 'pending' | 'deactivated'

export interface RoleFilterOption {
  id: UserAccountRoles | string
  name: string
}

export interface UserTableActions {
  onEdit?: (user: ManageUserTableRow) => void
  onDeactivate?: (userId: number) => void
  onResendInvitation?: (userId: number) => void
  onRevokeInvitation?: (userId: number) => void
  onReactivate?: (userId: number) => void
}

export const createSortableHeader = (title: string) => ({ column }: { column: Column<ManageUserTableRow> }) => {
  const isSorted = column.getIsSorted()
  const sortIconStyling = isSorted === 'asc' ? 'text-green-500' : isSorted === 'desc' ? 'text-red-500 !rotate-0' : ''

  return h('div', { class: 'flex justify-between items-center w-full' }, [
    h('span', { class: 'text-sm text-slate-800 font-semibold capitalize' }, title),
    h(Button, {
      class: 'text-main-primary',
      variant: 'ghost',
      onClick: () => column.toggleSorting(column.getIsSorted() === 'asc'),
    }, () => h(BarsArrowUp, { customStyle: `size-5 rotate-180 ${sortIconStyling}` })),
  ])
}

export const roleFilterData: RoleFilterOption[] = [
  { id: UserAccountRoles.ADMIN, name: 'Admin' },
  { id: UserAccountRoles.CONTRIBUTOR_ALL_ORG_DATA, name: 'Contributor (All Data)' },
  { id: UserAccountRoles.CONTRIBUTOR_ASSIGNED_DATA, name: 'Contributor (Assigned)' },
  { id: UserAccountRoles.VIEWER, name: 'Viewer' },
]

export const createBaseColumns = (): ColumnDef<ManageUserTableRow>[] => {
  const columnSize = 250

  return [
    {
      accessorKey: 'name',
      id: 'name',
      size: columnSize,
      enableSorting: true,
      header: createSortableHeader('name'),
      cell: ({ row, column }) => {
        return h('span', { class: 'text-left text-md text-foreground no-localize-translate' },
          row.getValue(column.id),
        )
      },
    },
    {
      accessorKey: 'email',
      id: 'email',
      size: columnSize,
      enableSorting: false,
      header: () => h('span', { class: 'text-sm text-slate-800 font-semibold capitalize' }, 'email'),
      cell: ({ row, column }) => {
        return h('span', { class: 'text-left text-md text-foreground no-localize-translate' },
          row.getValue(column.id),
        )
      },
    },
    {
      accessorKey: 'roles_display',
      id: 'role',
      size: columnSize,
      enableSorting: false,
      header: ({ column }) => h(DataTableFilterableHeader, {
        column,
        title: 'user role',
        filterData: roleFilterData,
        sort: false,
        isNotCommaSeperated: true,
      }),
      cell: ({ row, column }) => {
        return h('span', { class: 'text-left text-md text-foreground' },
          row.getValue(column.id),
        )
      },
    },
  ]
}
